import { BaseLayout, ContainedRow } from '../../layouts';
import { IncomingMessage, ServerResponse } from 'http';

import Cookies from 'cookies';
import { ShowAllProducts } from '../../components/ShowAllProducts';
import { TopMainBanner } from '../../shared/TopMainBanner';
import { getMarketplace } from '../../api/marketplace';
import { getStoreId } from '../../api/server/getStoreId';
import { initialFetch } from '../../api/server/initialFetch';
import { retrieveAllProducts } from '../../api/products';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { state } from '../../types/store/state';
import type { staticProps } from '../../types/pages/props';
import { uiStateMap } from '../../utils/uiStateMap';
import type { uiType } from '../../types/ui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';

const Products = () => {
  const { t } = useTranslation();
  const metatags = useSelector((state: state) => state.ui.metatags);
  const domain = useSelector((state: state) => state.ui.domain);
  const storeName = useSelector((state: state) => state.ui.name);

  const breadcrumbs = [
    {
      name: `${t('breadCrumbs.home.title')}`,
      url: '/',
    },
    {
      name: `${t('breadCrumbs.products.title')}`,
      url: '/products/',
    },
  ];

  return (
    <BaseLayout
      {...metatags}
      domain={domain}
      storeName={storeName}
      title={`${storeName} - All products`}
    >
      <TopMainBanner
        title={t('products.showAll.title')}
        isCompact={false}
        breadcrumbs={breadcrumbs}
      />
      <ContainedRow>
        <ShowAllProducts />
      </ContainedRow>
    </BaseLayout>
  );
};

export const getServerSideProps = async (context: staticProps) => {
  try {
    const translations = await serverSideTranslations(context.locale);
    const marketplace = await getMarketplace(context.req.headers.host);
    const cookies = new Cookies(context.req as unknown as IncomingMessage, context.res as ServerResponse);
    const sessionCookie = cookies.get('user_token');

    if (!marketplace.data.length) {
      return {
        props: {
          ...translations,
        },
        notFound: true,
      };
    }

    const marketplaceMap: uiType = uiStateMap(marketplace);
    const branchStoreId = await getStoreId(sessionCookie, marketplaceMap.storeId, marketplaceMap.businessId);

    if (branchStoreId === 0) {
      return {
        notFound: true,
      };
    }

    const response = await initialFetch(branchStoreId);

    let products;

    try {
      products = await retrieveAllProducts({
        id: marketplaceMap.storeId,
        page: context.query.page ? context.query.page : '1',
      });
    } catch (error: any) {
      if (error.response.status === 404) {
        return {
          notFound: true,
        };
      }
      throw new Error(error);
    }

    const preloadedState = JSON.parse(
      JSON.stringify({
        ui: marketplaceMap,
        categories: response.categories.results,
        branchStore: response.branchStoreDetail,
        branchStoreSchedule: response.branchStoreSchedule.results,
        products,
      })
    );

    return {
      props: {
        ...translations,
        preloadedState: preloadedState,
      },
    };
  } catch (error: any) {
    throw new Error(error);
  }
};

export default Products;
